import { showSubmitAppealState } from '@/atoms';
import { useSubmitAppeal } from '@/services/exam-appeals';
import { AppealType } from '@/types/examination.types';
import { Button, FileInput, Group, Stack, Text, Textarea, Badge, Select } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useState, useEffect } from 'react';
import { useRecoilState } from 'recoil';
import { useTranslation } from 'react-i18next';
import MainModal from '../MainModal';

type AppealTypeOption = 'theory' | 'practical';

export default function SubmitAppealModal() {
    const { t } = useTranslation();
    const [show, setShow] = useRecoilState(showSubmitAppealState);
    const { mutate: submitAppeal, isPending: submittingAppeal } = useSubmitAppeal();
    const [appealReason, setAppealReason] = useState('');
    const [supportingDocument, setSupportingDocument] = useState<File | null>(null);
    const [selectedAppealType, setSelectedAppealType] = useState<AppealTypeOption | null>(null);
    const [documentError, setDocumentError] = useState<string | null>(null);

    const handleClose = () => {
        setShow(null);
        setAppealReason('');
        setSupportingDocument(null);
        setSelectedAppealType(null);
        setDocumentError(null);
    }

    const handleDocumentChange = (file: File | null) => {
        setDocumentError(null);
        if (file && file.size > 5 * 1024 * 1024) {
            setDocumentError('File size must not exceed 5MB');
            return;
        }
        setSupportingDocument(file);
    };

    // Determine if both theory and practical results are available
    const hasBothResults = Boolean(show?.result && show?.practicalResult);
    
    // Initialize selected appeal type when modal opens
    useEffect(() => {
        if (show?.show) {
            if (hasBothResults) {
                // When both are available, start with no selection
                setSelectedAppealType(null);
            } else if (show?.result) {
                setSelectedAppealType('theory');
            } else if (show?.practicalResult) {
                setSelectedAppealType('practical');
            }
        }
    }, [show?.show, hasBothResults, show?.result, show?.practicalResult]);

    const handleSubmitAppeal = () => {
        if (!appealReason.trim()) {
            notifications.show({
                title: t('examinations.appealValidationError'),
                message: t('examinations.appealReasonRequired'),
                color: 'red'
            });
            return;
        }

        // When both results are available, require type selection
        if (hasBothResults && !selectedAppealType) {
            notifications.show({
                title: t('examinations.appealValidationError'),
                message: t('examinations.appealTypeRequired'),
                color: 'red'
            });
            return;
        }

        // Validate that exactly one of result or practicalResult is provided
        if (!show?.result && !show?.practicalResult) {
            notifications.show({
                title: t('examinations.appealValidationError'),
                message: t('examinations.appealNoResultError'),
                color: 'red'
            });
            return;
        }

        const appealData: any = {
            appealReason: appealReason.trim(),
            supportingDocument: supportingDocument || undefined
        };

        // Determine which result to use based on selection or availability
        if (hasBothResults) {
            if (selectedAppealType === 'theory' && show?.result) {
                appealData.examResultId = show.result.id;
            } else if (selectedAppealType === 'practical' && show?.practicalResult) {
                appealData.practicalExamResultId = show.practicalResult.id;
            } else {
                notifications.show({
                    title: t('examinations.appealValidationError'),
                    message: t('examinations.appealTypeRequired'),
                    color: 'red'
                });
                return;
            }
        } else if (show?.result) {
            appealData.examResultId = show.result.id;
        } else if (show?.practicalResult) {
            appealData.practicalExamResultId = show.practicalResult.id;
        }

        submitAppeal(appealData, {
            onSuccess: () => {
                notifications.show({
                    title: t('examinations.appealSuccessTitle'),
                    message: t('examinations.appealSuccessMessage'),
                    color: 'green'
                });
                handleClose();
            }
        });
    };

    // Determine appeal type and scores based on selection
    const currentAppealType = hasBothResults 
        ? (selectedAppealType === 'theory' ? AppealType.THEORY : selectedAppealType === 'practical' ? AppealType.PRACTICAL : null)
        : (show?.result ? AppealType.THEORY : show?.practicalResult ? AppealType.PRACTICAL : null);
    
    const examTitle = show?.result?.examTitle || show?.practicalResult?.examScheduleTitle || '';
    const theoryScore = show?.result?.scorePercentage || show?.result?.sessionScorePercentage || 0;
    const practicalScore = show?.practicalResult?.scorePercentage || 0;

    // Get the score to display based on selected type
    const displayScore = hasBothResults
        ? (selectedAppealType === 'theory' ? theoryScore : selectedAppealType === 'practical' ? practicalScore : 0)
        : (show?.result ? theoryScore : practicalScore);

    // Appeal type options for select dropdown
    const appealTypeOptions = [
        { value: 'theory', label: t('examinations.appealTypeMultipleChoice') },
        { value: 'practical', label: t('examinations.appealTypePractical') }
    ];

    return (
        <MainModal
            open={Boolean(show?.show)}
            onClose={handleClose}
            title={t('examinations.appealModalTitle')}
            size="md"
        >
            <Stack gap="md">
                {(show?.result || show?.practicalResult) && (
                    <div className="p-3 bg-gray-50 rounded-md">
                        <Group gap="xs" align="center">
                            <Text size="sm" fw={500}>{t('examinations.examTitle')}: {examTitle}</Text>
                            {currentAppealType && (
                                <Badge
                                    color={currentAppealType === AppealType.THEORY ? 'blue' : 'green'}
                                    variant="light"
                                    size="sm"
                                >
                                    {currentAppealType === AppealType.THEORY 
                                        ? t('examinations.appealTypeMultipleChoice')
                                        : t('examinations.appealTypePractical')}
                                </Badge>
                            )}
                        </Group>
                        
                        {/* Show both scores when both results are available */}
                        {hasBothResults ? (
                            <Stack gap="xs" mt="xs">
                                <Text size="sm" c="dimmed">
                                    {t('examinations.appealTheoryScore')}: {theoryScore.toFixed(1)}%
                                </Text>
                                <Text size="sm" c="dimmed">
                                    {t('examinations.appealPracticalScore')}: {practicalScore.toFixed(1)}%
                                </Text>
                            </Stack>
                        ) : (
                            <Text size="sm" c="dimmed">
                                {t('examinations.score')}: {displayScore.toFixed(1)}%
                            </Text>
                        )}
                        
                        {show?.result && (
                            <Text size="xs" c="dimmed" mt="xs">
                                {t('examinations.appealTheoryNote')}
                            </Text>
                        )}
                    </div>
                )}

                {/* Appeal Type Selection - Show when both results are available */}
                {hasBothResults && (
                    <Select
                        label={t('examinations.appealTypeSelection')}
                        description={t('examinations.appealTypeSelectionDescription')}
                        placeholder={t('examinations.appealTypeSelection')}
                        value={selectedAppealType}
                        onChange={(value) => setSelectedAppealType(value as AppealTypeOption | null)}
                        data={appealTypeOptions}
                        required
                    />
                )}

                <Textarea
                    label={t('examinations.appealReasonLabel')}
                    placeholder={t('examinations.appealReasonPlaceholder')}
                    value={appealReason}
                    onChange={(e) => setAppealReason(e.target.value)}
                    minRows={4}
                    maxRows={8}
                    required
                />

                <FileInput
                    label={t('examinations.appealSupportingDocumentLabel')}
                    placeholder={t('examinations.appealSupportingDocumentPlaceholder')}
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    value={supportingDocument}
                    onChange={handleDocumentChange}
                    error={documentError}
                />

                <Group justify="flex-end" gap="md">
                    <Button
                        variant="outline"
                        onClick={handleClose}
                    >
                        {t('examinations.appealCancelButton')}
                    </Button>
                    <Button
                        color="blue"
                        loading={submittingAppeal}
                        onClick={handleSubmitAppeal}
                        disabled={!appealReason.trim() || (hasBothResults && !selectedAppealType)}
                    >
                        {t('examinations.appealSubmitButton')}
                    </Button>
                </Group>
            </Stack>
        </MainModal>
    )
}
